import { useEffect, useRef, useState } from 'react';
import { IntegrationCard } from './integration-card';
import googleAds from '/google-ads.svg';
import hubspot from '/hubspot.png';
import { Button } from '@/components/ui/button';
import { GoogleAdAccountSelectionModal } from '@/components/glitch-ui/google-ad-account-selection-modal';
import { useGoogleAdsConnection } from '@/hooks/useGoogleAdsConnection';
import { MetaAdAccountSelectionModal } from '@/components/glitch-ui/meta-ad-account-selection-modal';
import { HubSpotIntegrationModal } from './hubspot-integration-modal';
import { MetaIntegrationModal } from './meta-integration-modal';
import { useAtomValue } from 'jotai';
import { googleAdsAccountAtom, organizationDataAtom } from '@/store';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  googleIntegrationScopesQueryOptions,
  googleServicesStatusQueryOptions,
  useHubSpotIntegrationStatus,
  useMetaIntegrationStatus,
  userProfileQueryOptions,
} from '@/lib/query-options';
import { useTestMode } from '@/hooks/useTestMode';
import { shouldAutoOpenMetaAccountPicker } from '@/lib/meta';
import { AlertCircle } from 'lucide-react';
import type { GoogleService } from '@/lib/types';
import { GoogleServiceCard } from './google-service-card';

export const IntegrationSettings = () => {
  // Google Ads is one workflow (#6668): Connect on the card runs the OAuth popup and, once
  // connected, opens the account picker; Manage opens the same picker; Disconnect sits
  // beside Manage. No Refresh: a broken token renders the card as not connected, and the
  // picker re-enumerates accounts on open. The only modal is the picker.
  const [isOpenGooglePicker, setIsOpenGooglePicker] = useState<boolean>(false);
  // The card's Connect opens the account picker afterwards; the Analytics and Tag Manager reconnect banner does not,
  // since the user is already on an account and only granting the extra permissions (#6879).
  const openPickerAfterConnect = useRef(true);
  const googleAdsConnection = useGoogleAdsConnection({
    onConnected: () => {
      if (openPickerAfterConnect.current) setIsOpenGooglePicker(true);
      openPickerAfterConnect.current = true;
    },
  });
  const connectGoogleAds = () => {
    openPickerAfterConnect.current = true;
    googleAdsConnection.connect();
  };
  const reconnectGoogleForServices = () => {
    openPickerAfterConnect.current = false;
    googleAdsConnection.connect();
  };
  const [isOpenHubSpotModal, setIsOpenHubSpotModal] = useState<boolean>(false);
  const [isOpenMetaModal, setIsOpenMetaModal] = useState<boolean>(false);
  const [isOpenMetaPicker, setIsOpenMetaPicker] = useState<boolean>(false);

  // Slack is a separate coming-soon integration, still test-mode-only (not tied to Meta).
  const isTestMode = useTestMode();
  const selectedGoogleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const { data: hubspotStatus } = useHubSpotIntegrationStatus();
  // The backend's meta-integration-enabled flag is the access control; the card shows for everyone (#6678).
  const { data: metaStatus } = useMetaIntegrationStatus();
  const role = useAtomValue(organizationDataAtom)?.membership?.role;
  // Google Analytics and Tag Manager stay hidden until their flag is on (#6879): the scope list carries it.
  const { data: consent } = useSuspenseQuery(googleIntegrationScopesQueryOptions());
  const analyticsTagManagerEnabled = consent.analytics_tag_manager_enabled;
  const googleServices = useQuery(googleServicesStatusQueryOptions(analyticsTagManagerEnabled));
  const serviceStatus = (service: GoogleService) =>
    googleServices.data?.services.find((status) => status.service === service);

  // Revoked Google token (#5935): /user/me reports the broken flag; the card renders
  // disconnected so the user reconnects (the account modal carries the OAuth refresh CTA).
  const { data: userProfile } = useQuery(userProfileQueryOptions());
  const googleConnectionBroken = userProfile?.google_connection_broken === true;

  // The card keys on the connection, not the selected account (#6668). Connected with no
  // account is a legitimate state (a Meta-only organisation, say); the card says so.
  // Nothing auto-opens the picker on page load.
  const googleConnected = userProfile?.google_ads_connected === true && !googleConnectionBroken;

  // Auto-open the Meta ad-account picker when the user lands here connected but without a chosen
  // ad account — covers two paths: (1) fresh OAuth that just redirected from /meta/callback with
  // multiple ad accounts, and (2) stale connections from before this fix. Ref guards against
  // reopening after a manual dismiss in the same session; a hard refresh re-arms (which is desired
  // since the card is unusable without a selection). #4173 Chunk 11.
  const hasAutoOpenedMetaPicker = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedMetaPicker.current) return;
    if (shouldAutoOpenMetaAccountPicker(metaStatus)) {
      hasAutoOpenedMetaPicker.current = true;
      setIsOpenMetaPicker(true);
    }
  }, [metaStatus]);

  const handleCloseHubSpotModal = () => setIsOpenHubSpotModal(false);
  const handleCloseMetaModal = () => setIsOpenMetaModal(false);
  const handleCloseMetaPicker = () => setIsOpenMetaPicker(false);

  const getGoogleAdsConnectionData = () => {
    // A broken (#5935) or voluntarily-disconnected (#5936) connection renders as
    // disconnected — showing the account as connected while every Google Ads call
    // fails is exactly the silent state those issues remove.
    if (!googleConnected) return undefined;

    // Same placeholder the Meta card uses for connected-but-unselected.
    return {
      connectedDate: new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }),
      accountId: selectedGoogleAdsAccount?.googleads_account_id || 'Not selected',
      apiKey: selectedGoogleAdsAccount?.name || '—',
      serviceName: 'Google Ads',
    };
  };

  const getHubSpotConnectionData = () => {
    if (!hubspotStatus?.is_connected) return undefined;

    return {
      connectedDate: hubspotStatus.created_at
        ? new Date(hubspotStatus.created_at).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          })
        : 'Unknown',
      accountId: hubspotStatus.account_id || 'Unknown',
      apiKey: hubspotStatus.account_name || 'HubSpot Account',
      serviceName: 'HubSpot',
    };
  };

  const getMetaConnectionData = () => {
    if (!metaStatus?.is_connected) return undefined;

    // The card mirrors Google Ads / HubSpot: shows the *account*, not the connecting human.
    // The auto-opened picker (above) plus the Change-Account-as-picker re-route below guarantee a
    // connected card normally has a selection by the time it renders; if a user dismisses the modal
    // and returns later, the "Not selected" placeholder is the transient state until they pick.
    const selected = metaStatus.selected_ad_account;
    const accountId = selected?.account_id || 'Not selected';
    const accountName = selected
      ? selected.currency
        ? `${selected.name || selected.account_id} · ${selected.currency}`
        : selected.name || selected.account_id
      : '—';

    return {
      connectedDate: metaStatus.created_at
        ? new Date(metaStatus.created_at).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          })
        : 'Unknown',
      accountId,
      apiKey: accountName,
      serviceName: 'Meta Ads',
    };
  };

  // "Change Account" on the Meta card is dual-purpose: if there's no selected ad account yet (rare,
  // dismiss-modal-and-came-back case), it opens the picker so the user can fix the unusable state;
  // otherwise it opens the HubSpot-parity integration modal (Refresh Connection / Disconnect).
  const handleMetaCardClick = () => {
    if (metaStatus?.is_connected && !metaStatus.selected_ad_account) {
      setIsOpenMetaPicker(true);
    } else {
      setIsOpenMetaModal(true);
    }
  };

  return (
    <div className="space-y-1">
      {analyticsTagManagerEnabled && googleServices.data?.reconnect_suggested && (
        <div
          className="flex items-start gap-3 rounded-md border-l-4 border-[#290BD9] bg-[#EDE9FE] p-4"
          data-testid="google-services-reconnect-banner"
        >
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#290BD9]" />
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium text-gray-900">Google Analytics and Tag Manager Are Not Connected</p>
            <p className="text-sm text-gray-700">
              Connect your Google Analytics and Tag Manager accounts to check conversion tracking for this organization.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="text-gray-900"
            onClick={reconnectGoogleForServices}
            disabled={googleAdsConnection.isAuthorizing}
          >
            Connect Google
          </Button>
        </div>
      )}
      <div className="grid gap-1 grid-cols-3">
        <IntegrationCard
          title="Google Ads"
          description="Connect your Google Ads account to create, deploy and optimize search campaigns."
          logo={googleAds}
          isEnabled={googleConnected}
          connectData={getGoogleAdsConnectionData()}
          onToggle={() => (googleConnected ? setIsOpenGooglePicker(true) : connectGoogleAds())}
          actionLabel={!googleConnected && googleAdsConnection.isAuthorizing ? 'Connecting...' : undefined}
          actionDisabled={googleAdsConnection.isAuthorizing || googleAdsConnection.isDisconnecting}
          actions={
            googleConnected ? (
              <Button
                size="sm"
                variant="destructive"
                onClick={googleAdsConnection.disconnect}
                disabled={googleAdsConnection.isDisconnecting}
              >
                {googleAdsConnection.isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            ) : undefined
          }
        />

        {analyticsTagManagerEnabled &&
          (['google_analytics', 'tag_manager'] as const).map((service) => (
            <GoogleServiceCard key={service} service={service} status={serviceStatus(service)} role={role} />
          ))}

        <IntegrationCard
          title="HubSpot"
          description="Connect your HubSpot CRM to sync deal pipeline conversions with Google Ads and Meta."
          logo={hubspot}
          isEnabled={hubspotStatus?.is_connected || false}
          connectData={getHubSpotConnectionData()}
          onToggle={() => setIsOpenHubSpotModal(true)}
        />

        <IntegrationCard
          title="Meta Ads"
          description="Connect your Meta Business account to manage Facebook and Instagram ad campaigns."
          logo="/meta.png"
          isEnabled={metaStatus?.is_connected || false}
          connectData={getMetaConnectionData()}
          onToggle={handleMetaCardClick}
        />


        {isTestMode && (
          <IntegrationCard
            title="Slack"
            description="Send campaign performance reports and alerts directly to your Slack channels."
            logo="/slack.svg"
            isEnabled={false}
            connectData={undefined}
            onToggle={() => {}}
            comingSoon
          />
        )}
      </div>


      <GoogleAdAccountSelectionModal isOpen={isOpenGooglePicker} onClose={() => setIsOpenGooglePicker(false)} />

      <HubSpotIntegrationModal
        isOpen={isOpenHubSpotModal}
        onClose={handleCloseHubSpotModal}
        integrationStatus={hubspotStatus || null}
      />

      <MetaIntegrationModal
        isOpen={isOpenMetaModal}
        onClose={handleCloseMetaModal}
        integrationStatus={metaStatus || null}
      />

      {/*
        Auto-opens immediately after Meta OAuth completes (no selected ad account yet) — the same
        modal the user-menu's "Ad Accounts → Meta" item opens. Closing without picking is allowed;
        a hard refresh re-opens it so a connected-but-unusable card never persists.
      */}
      <MetaAdAccountSelectionModal isOpen={isOpenMetaPicker} onClose={handleCloseMetaPicker} />
    </div>
  );
};
